// Lib
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

// Components
import { ImageModalEditorContent, type ImageModalEditorProps } from '../../content/ImageModalEditor';
import ImageModalBackgroundRemovalToast from '../toolbar/ImageModalBackgroundRemovalToast';

// Hooks
import { useImageModalBackgroundRemovalProgress } from './hooks/useImageModalBackgroundRemovalProgress';
import { useImageModalV2BackgroundRemovalState } from './hooks/useImageModalV2BackgroundRemovalState';

// Utils
import { getElementId, getImageProp } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { asObject } from '../../../../../../common/utils/immutableHelper';
import {
    applyStagedBackgroundRemoval,
    isBackgroundRemovalActive,
} from '../toolbar/imageModalBackgroundRemovalUtils';
import type { MNImageContent } from '../../../../../../common/elements/elementModelTypes';

/**
 * V2 image editor shell: owns background-removal progress, phase state, toast, and toolbar behaviour.
 */
const ImageModalV2Editor: React.FC<ImageModalEditorProps> = ({ toolbarMode = 'edit', ...props }) => {
    const [editorIsReady, setEditorIsReady] = useState(false);
    const elementId = getElementId(props.element);

    // Staged intent for the removed-background variant. The processed source is cached on the element
    // immediately to avoid repeat provider calls, but applying it is previewed locally and only persisted on save.
    const [backgroundRemovalStagedActive, setBackgroundRemovalStagedActive] = useState(() =>
        isBackgroundRemovalActive(props.element),
    );

    // Read the latest element without re-running the reset effect on every element change (cache persist,
    // progress, etc.) — only a different image should reset the staged intent.
    const elementRef = useRef(props.element);
    elementRef.current = props.element;

    // Reset the staged intent only when a different image opens, not when the element merely changes (e.g. when
    // the source cache is persisted after the provider returns).
    const previousElementIdRef = useRef(elementId);
    useLayoutEffect(() => {
        if (previousElementIdRef.current === elementId) return;
        previousElementIdRef.current = elementId;
        setBackgroundRemovalStagedActive(isBackgroundRemovalActive(elementRef.current));
    }, [elementId]);

    // Preview the staged variant by applying it in-memory; the stored element stays untouched until save.
    const element = useMemo(
        () => applyStagedBackgroundRemoval(props.element, backgroundRemovalStagedActive),
        [props.element, backgroundRemovalStagedActive],
    );

    const { backgroundRemovalApiProgress, backgroundRemovalUploadProgress, setBackgroundRemovalProgress } =
        useImageModalBackgroundRemovalProgress(elementId);
    const image = asObject<MNImageContent>(getImageProp(element));
    const editorImageLoadKey = JSON.stringify([elementId, image?.original, image?.large, image?.regular, image?.thumb]);
    const backgroundRemovalState = useImageModalV2BackgroundRemovalState({
        editorImageLoadKey,
        editorIsReady,
        backgroundRemovalApiProgress,
        backgroundRemovalUploadProgress,
    });
    const toolbarClassName = classNames('ImageEditorModalToolbar', 'ImageEditorModalToolbar-v2', {
        'toast-visible': backgroundRemovalState.shouldHideToolbar,
    });

    const handleEditorReadyChange = useCallback(
        (ready: boolean) => {
            setEditorIsReady(ready);
            backgroundRemovalState.handleEditorImageReady(ready);
        },
        [backgroundRemovalState.handleEditorImageReady],
    );

    return (
        <ImageModalEditorContent
            {...props}
            element={element}
            editorIsReady={editorIsReady}
            isV2Toolbar
            toolbarMode={toolbarMode}
            showImageReplacementWipe={backgroundRemovalState.shouldAnimateEditorImageReplacement}
            showEditorLoadingPreview={backgroundRemovalState.shouldShowEditorLoadingPreview}
            toolbarClassName={toolbarClassName}
            toast={<ImageModalBackgroundRemovalToast toast={backgroundRemovalState.toast} />}
            backgroundRemovalProgress={backgroundRemovalState.buttonProgress}
            backgroundRemovalControlsDisabled={backgroundRemovalState.controlsDisabled}
            backgroundRemovalStagedActive={backgroundRemovalStagedActive}
            backgroundRemovalStoredActive={isBackgroundRemovalActive(props.element)}
            onChangeBackgroundRemovalStagedActive={setBackgroundRemovalStagedActive}
            onEditorReadyChange={handleEditorReadyChange}
            setBackgroundRemovalProgress={setBackgroundRemovalProgress}
        />
    );
};

export default ImageModalV2Editor;
